import { OpusAgentChatTurnStatus } from "@src/types.generated";

// Structural payload shape shared by the fragment codegen emits and by
// tests — mirrors the server's OpusAgentEventPayload. Exactly one kind is
// set per payload; a payload where all are null is a kind this client
// predates (render fallbackText, else skip).
export type OpusAgentEventPayloadLike = {
  assistantText?: { text: string } | null;
  thinking?: { text: string } | null;
  toolCall?: {
    callId?: string | null;
    toolName: string;
    label: string;
    status: string;
    // Report name / path / pattern, when the relay attached one.
    detail?: string | null;
    errorSummary?: string | null;
    // The GraphQL mutations a write tool ran, on its SUCCEEDED event (see
    // agentMutationRefetch.ts).
    mutationNames?: readonly string[] | null;
  } | null;
  planUpdate?: {
    done: number;
    total: number;
    activeStep?: string | null;
    // Absent on payloads written before items existed.
    items?: { text: string; status: string }[] | null;
  } | null;
  error?: { message: string } | null;
  // The agent condensed earlier conversation to stay within its context
  // limits; the client shows a quiet activity row (no numbers).
  contextCompacted?: { summaryChars: number } | null;
  // A read-only plan the agent shows before a big build, then waits for the
  // person's OK (see planProposals.ts).
  planProposal?: OpusAgentPlanProposalLike | null;
  // Tap-to-answer questions the agent asks before it plans, then waits for
  // an ordinary next message (see questionPrompts.ts).
  questions?: { questions: readonly OpusAgentQuestionLike[] } | null;
  fallbackText?: string | null;
};

export type OpusAgentQuestionLike = {
  id: string;
  text: string;
  options: readonly string[];
  multiSelect: boolean;
};

export type OpusAgentPlanProposalLike = {
  version: number;
  title: string;
  kind: string;
  summary?: string | null;
  audience?: string | null;
  language?: string | null;
  sources: readonly string[];
  items: readonly {
    title: string;
    kind?: string | null;
    summary?: string | null;
    points: readonly string[];
    isNew: boolean;
    // Consecutive items with the same section form one group; null or
    // absent = ungrouped.
    section?: string | null;
  }[];
  assumptions: readonly string[];
  changes: readonly string[];
};

export type OpusAgentEventLike = {
  index: number;
  payload: OpusAgentEventPayloadLike;
};

export type OpusAgentEventKind =
  | "assistantText"
  | "thinking"
  | "toolCall"
  | "planUpdate"
  | "error"
  | "contextCompacted"
  | "planProposal"
  | "questions"
  | "unknown";

export const getEventKind = (
  payload: OpusAgentEventPayloadLike,
): OpusAgentEventKind => {
  if (payload.assistantText) return "assistantText";
  if (payload.thinking) return "thinking";
  if (payload.toolCall) return "toolCall";
  if (payload.planUpdate) return "planUpdate";
  if (payload.error) return "error";
  if (payload.contextCompacted) return "contextCompacted";
  if (payload.planProposal) return "planProposal";
  if (payload.questions) return "questions";
  return "unknown";
};

const SCHEMA_TOOLS = new Set([
  "read_graphql_schema",
  "read_sql_schema",
  "get_schema",
  "get_sql_schema",
]);
const QUERY_TOOLS = new Set(["run_graphql_query", "run_sql", "make_query"]);

// Schema and query tools get a prose phrase — never the identifier. Other
// tools stay `toolName`, or `toolName:detail` when the relay attached what
// the call is about. NOT shortened here — the row fills the drawer's real
// width and CSS ellipsises what does not fit.
export const toolRowLabel = (
  toolName: string,
  detail: string | null | undefined,
): string => {
  if (SCHEMA_TOOLS.has(toolName)) return "Exploring data";
  if (QUERY_TOOLS.has(toolName)) return "Querying records";
  return detail ? `${toolName}:${detail}` : toolName;
};

// Poll results arrive strictly after afterIndex, but a send response and an
// in-flight poll can race; dedupe by index and keep events ascending.
export const mergeEvents = <T extends { index: number }>(
  existing: T[],
  incoming: T[],
): T[] => {
  const byIndex = new Map<number, T>();
  for (const event of [...existing, ...incoming]) {
    if (!byIndex.has(event.index)) {
      byIndex.set(event.index, event);
    }
  }
  return [...byIndex.values()].sort((a, b) => a.index - b.index);
};

export const highestEventIndex = (events: { index: number }[]): number =>
  events.reduce((max, event) => Math.max(max, event.index), -1);

// A tool call is two events — RUNNING at call time, SUCCEEDED/FAILED when
// the result lands — sharing a callId. Fold them into one event that changes
// status in place, keeping the RUNNING event's label, id and index (stable
// key). Events without a callId (written before it existed) stay as they are.
export const foldToolCallEvents = <T extends OpusAgentEventLike>(
  events: T[],
): T[] => {
  const folded: T[] = [];
  const chipPosition = new Map<string, number>();
  for (const event of events) {
    const callId = event.payload.toolCall?.callId;
    if (!callId) {
      folded.push(event);
      continue;
    }
    const position = chipPosition.get(callId);
    if (position === undefined) {
      chipPosition.set(callId, folded.length);
      folded.push(event);
      continue;
    }
    const chip = folded[position];
    folded[position] = {
      ...chip,
      payload: {
        ...chip.payload,
        toolCall: {
          ...chip.payload.toolCall!,
          status: event.payload.toolCall!.status,
          errorSummary:
            event.payload.toolCall!.errorSummary ??
            chip.payload.toolCall!.errorSummary,
        },
      },
    };
  }
  return folded;
};

// Display status for a tool call: a turn that reached a terminal status
// settles any call still marked RUNNING.
export const resolveToolCallStatus = (
  event: OpusAgentEventLike,
  turnStatus: OpusAgentChatTurnStatus,
): string => {
  const toolCall = event.payload.toolCall;
  if (!toolCall) return "";
  if (toolCall.status !== "RUNNING") return toolCall.status;
  if (turnStatus === OpusAgentChatTurnStatus.Running) return "RUNNING";
  return turnStatus === OpusAgentChatTurnStatus.Succeeded
    ? "SUCCEEDED"
    : "FAILED";
};

// The Ask Opus tools that make or change something (scadmin
// mcp_server/tools/registry.py: the specs with read_only=False), each with
// the kind of output it makes.
export type OutputStepKind =
  | "course"
  | "module"
  | "checklist"
  | "checkin"
  | "document"
  | "upload"
  | "change";

const OUTPUT_STEP_KIND: Record<string, OutputStepKind> = {
  build_course: "course",
  build_module: "module",
  build_checklist: "checklist",
  build_checkin: "checkin",
  build_document: "document",
  upload_media: "upload",
  run_graphql_mutation: "change",
};

export const OPUS_AGENT_WRITE_TOOLS: ReadonlySet<string> = new Set(
  Object.keys(OUTPUT_STEP_KIND),
);

/**
 * Whether a turn created content (a harder job): a build tool that
 * succeeded. Only these end with the spin; uploads, data changes and plain
 * answers just land.
 */
export const turnCreatedContent = (
  events: readonly OpusAgentEventLike[],
): boolean =>
  events.some((event) => {
    const call = event.payload.toolCall;
    const kind = call ? OUTPUT_STEP_KIND[call.toolName] : undefined;
    return call?.status === "SUCCEEDED" && !!kind && BUILD_KINDS.has(kind);
  });

// Did the turn do real work: create something, find something (a library
// search, a data query, a report) or carry out an action? Any tool call
// that succeeded, except reading a schema, which only tells the agent how
// the data is shaped (SCHEMA_TOOLS). A plain answer did none of it.
// Decides whether the turn finishes with done.
export const turnDidWork = (events: readonly OpusAgentEventLike[]): boolean =>
  events.some((event) => {
    const call = event.payload.toolCall;
    return call?.status === "SUCCEEDED" && !SCHEMA_TOOLS.has(call.toolName);
  });

// ---------- output steps ----------
// What the agent makes or changes in a turn (one step per write call). The
// latest shows as a line under the status row while the turn works; none of
// it stays in the chat. Lookups, queries, plans and the model's own
// reasoning never show.

// STOPPED: the person stopped the turn while this was still running.
export type OutputStepStatus = "RUNNING" | "SUCCEEDED" | "FAILED" | "STOPPED";

const BUILD_KINDS: ReadonlySet<OutputStepKind> = new Set([
  "course",
  "module",
  "checklist",
  "checkin",
  "document",
]);

export interface OutputStep {
  // Its first event's index (steps come out in the order they started).
  index: number;
  kind: OutputStepKind;
  title: string;
  status: OutputStepStatus;
  errorSummary: string | null;
}

// The relay labels a build "Building course draft: <name>"; the step shows
// the name. A build label without one names nothing (null), so the step is
// never titled with the generic tool label. A mutation's label is shown as is.
const stepTitle = (
  kind: OutputStepKind,
  label: string,
  detail: string | null | undefined,
): string | null => {
  const named = label.match(/^(?:Building [^:]+|Uploading): (.+)$/);
  if (named) return named[1];
  if (kind === "change") return label.trim() || detail || null;
  return null;
};

export const outputSteps = <T extends OpusAgentEventLike & { id?: string }>(
  events: T[],
  turnStatus: OpusAgentChatTurnStatus,
): OutputStep[] => {
  const steps: OutputStep[] = [];
  for (const event of foldToolCallEvents(events)) {
    const call = event.payload.toolCall;
    const kind = call ? OUTPUT_STEP_KIND[call.toolName] : undefined;
    if (!call || !kind) continue;
    const stopped =
      call.status === "RUNNING" &&
      turnStatus === OpusAgentChatTurnStatus.Aborted;
    const status = stopped
      ? "STOPPED"
      : (resolveToolCallStatus(event, turnStatus) as OutputStepStatus);
    const step: OutputStep = {
      index: event.index,
      kind,
      title: stepTitle(kind, call.label, call.detail) ?? "",
      status,
      errorSummary: status === "FAILED" ? call.errorSummary ?? null : null,
    };
    // A nameless build is the agent working on the draft it just started
    // (it builds a course in more than one call): fold it into that step.
    // Uploads and changes are each their own thing, so only builds fold.
    const previous = [...steps].reverse().find((c) => c.kind === kind);
    if (!step.title && BUILD_KINDS.has(kind) && previous) {
      previous.status = step.status;
      previous.errorSummary = step.errorSummary ?? previous.errorSummary;
      continue;
    }
    steps.push(step);
  }
  return steps;
};

/**
 * Blocks that still show as their own message: the reply, errors, plan
 * cards and question cards.
 */
export const isVisibleTurnBlock = (
  payload: OpusAgentEventPayloadLike,
): boolean => {
  const kind = getEventKind(payload);
  return (
    kind === "assistantText" ||
    kind === "error" ||
    kind === "planProposal" ||
    kind === "questions" ||
    (kind === "unknown" && !!payload.fallbackText)
  );
};

export const stepNoteIndexes = (
  events: readonly OpusAgentEventLike[],
  turnEnded: boolean,
): Set<number> => {
  const notes = new Set<number>();
  let toolCallFollows = false;
  for (const event of [...events].reverse()) {
    if (event.payload.toolCall) toolCallFollows = true;
    else if (event.payload.assistantText && toolCallFollows)
      notes.add(event.index);
  }
  const lastText = [...events]
    .reverse()
    .find((event) => event.payload.assistantText);
  if (turnEnded && lastText) notes.delete(lastText.index);
  return notes;
};

// ---------- what the turn is doing now ----------
// The status row under a running turn: the avatar's face and one short line
// for the current step. Until the turn does something the avatar looks
// around (idle: it breathes, glances about, blinks); then it shows its dots,
// thinking (three on a tilted ring) while the model reasons and working
// (four turning on a flat ring) while a tool runs or something is built,
// and looks around again between steps.

export type TurnActivityOrb = "idle" | "thinking" | "working";

export interface TurnActivity {
  orb: TurnActivityOrb;
  label: string;
}

const DATA_TOOLS = new Set([
  "run_graphql_query",
  "run_sql",
  "make_query",
  "read_graphql_schema",
  "read_sql_schema",
  "get_schema",
  "get_sql_schema",
]);

const ACTIVITY_FOR_KIND: Record<OutputStepKind, string> = {
  course: "Building a course",
  module: "Building a module",
  checklist: "Building a checklist",
  checkin: "Building a check-in",
  document: "Building a document",
  upload: "Uploading a file",
  change: "Making a change",
};

const toolActivityLabel = (toolName: string): string => {
  const kind = OUTPUT_STEP_KIND[toolName];
  if (kind) return ACTIVITY_FOR_KIND[kind];
  if (toolName === "library_deep_search") return "Searching your library";
  if (DATA_TOOLS.has(toolName)) return "Looking at your data";
  if (toolName === "run_report") return "Running a report";
  // suggest_reports is the retired name of get_playbooks (scadmin
  // mcp_server/tools/aliases.py); older turns still carry it.
  if (toolName === "get_playbooks" || toolName === "suggest_reports")
    return "Finding the right playbook";
  if (toolName === "list_recent_changes") return "Checking recent changes";
  return "Working";
};

export const turnActivity = (
  events: readonly OpusAgentEventLike[],
): TurnActivity => {
  const folded = foldToolCallEvents([...events]);
  const latest = [...folded].reverse().find((event) => {
    const kind = getEventKind(event.payload);
    return (
      kind === "thinking" || kind === "toolCall" || kind === "assistantText"
    );
  });
  if (!latest) return { orb: "idle", label: "Working" };
  if (latest.payload.thinking) return { orb: "thinking", label: "Thinking" };
  // Calls can run side by side: one finishing doesn't mean the turn rests
  // while another is still going. Show the latest one still running.
  const running = [...folded]
    .reverse()
    .find((event) => event.payload.toolCall?.status === "RUNNING");
  if (running)
    return {
      orb: "working",
      label: toolActivityLabel(running.payload.toolCall!.toolName),
    };
  return { orb: "idle", label: "Working" };
};
